import { useState, useRef, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { Modal } from '@/components/ui/Modal';
import { TextInput, Textarea } from '@components/ui';
import Select from '@workpay/ds-shared/components/ui/Select';
import Stepper from '@/components/ui/Stepper/Stepper';
import type { StepConfig } from '@/components/ui/Stepper/types';
import StepperModalFooter from '@/components/shared/StepperModalFooter/StepperModalFooter';
import useMultiStepForm from '@hooks/useMultiStepForm';
import { useTranslation } from '@hooks/useTranslation';
import { useCountries } from '@hooks/useCountries';
import { useNotificationStore } from '@stores/notificationStore';
import { dsTemplateService } from '@services/dsTemplateService';
import { dsFileService } from '@services/dsFileService';
import { getProductTypes, resolveCountries } from '@services/tenantService';
import { getLegalEntities } from '@services/legalEntityService';
import { getEmploymentTypes } from '@services/employmentTypeService';
import { getDocumentTypes } from '@services/documentTypeService';
import type { LegalEntityListItem } from '@/types/legalEntity';
import styles from './CreateTemplateModal.module.scss';

// ============================================
// TYPES
// ============================================

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  routeTo: string;
}

interface FormValues {
  name: string;
  description: string;
  countryId: string;
  legalEntityId: string;
  employmentTypeId: string;
  documentTypeId: string;
}

const DEFAULT_FORM_VALUES: FormValues = {
  name: '',
  description: '',
  countryId: '',
  legalEntityId: '',
  employmentTypeId: '',
  documentTypeId: '',
};

// ============================================
// COMPONENT
// ============================================

export function CreateTemplateModal({ isOpen, onClose, routeTo }: CreateTemplateModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);

  const [templateFormat, setTemplateFormat] = useState<'DOCX' | 'PDF'>('DOCX');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { register, control, watch, setValue, reset, formState: { errors } } = useForm<FormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  const watchedCountryId = watch('countryId');
  const watchedLegalEntityId = watch('legalEntityId');
  const watchedName = watch('name');

  // ============================================
  // STEPPER
  // ============================================

  const stepConfigs = useMemo<StepConfig[]>(() => [
    { id: 'scope', label: t('K_DS_STEP_SCOPE', 'Select Scope') },
    { id: 'setup', label: t('K_DS_STEP_SETUP', 'Template Setup') },
  ], [t]);

  const canProceedScope = !!(
    watch('countryId') &&
    watch('legalEntityId') &&
    watch('employmentTypeId') &&
    watch('documentTypeId')
  );

  const canCreate = !!(canProceedScope && watchedName?.trim() && selectedFile);

  const { currentStep, isFirstStep, isLastStep, goToNext, goToPrev, goToStep } = useMultiStepForm({
    totalSteps: stepConfigs.length,
    onValidateStep: async (stepIndex) => {
      if (stepIndex === 0) {
        if (!canProceedScope) {
          addNotification({ variant: 'critical', title: t('K_DS_VALIDATION_SELECT_SCOPE', 'Please select country, legal entity, employment type, and document type.') });
          return false;
        }
        return true;
      }
      return true;
    },
  });

  // ============================================
  // CASCADING DATA FETCHING
  // ============================================

  const { data: productTypesData } = useQuery({
    queryKey: ['productTypes', { size: 100 }],
    queryFn: () => getProductTypes({ size: 100 }),
    enabled: isOpen,
  });

  const eorProductType = useMemo(
    () => productTypesData?.content?.find((pt) => pt.productType === 'EOR'),
    [productTypesData],
  );

  const { data: resolvedCountriesData } = useQuery({
    queryKey: ['resolveCountries', eorProductType?.id],
    queryFn: () => resolveCountries([eorProductType!.id]),
    enabled: isOpen && !!eorProductType?.id,
  });

  const { countries, isLoading: countriesLoading } = useCountries();

  const countryOptions = useMemo(() => {
    if (!resolvedCountriesData?.[0]?.countries) return [];
    const resolvedCountryIds = new Set(
      resolvedCountriesData[0].countries.map((c) => c.countryId),
    );
    return countries
      .filter((c) => resolvedCountryIds.has(c.id))
      .map((c) => ({ label: c.name, value: c.id }));
  }, [resolvedCountriesData, countries]);

  const { data: legalEntitiesData, isLoading: legalEntitiesLoading } = useQuery({
    queryKey: ['legalEntities', { countryId: watchedCountryId, size: 100 }],
    queryFn: () => getLegalEntities({ size: 100 }, { filters: { countryId: watchedCountryId } }),
    enabled: isOpen && !!watchedCountryId,
  });

  const legalEntityOptions = useMemo(
    () => (legalEntitiesData?.content ?? []).map((le) => ({ label: le.name, value: le.id })),
    [legalEntitiesData],
  );

  const selectedEntity: LegalEntityListItem | undefined = useMemo(
    () => legalEntitiesData?.content?.find((le) => le.id === watchedLegalEntityId),
    [legalEntitiesData, watchedLegalEntityId],
  );

  const { data: employmentTypesData, isLoading: employmentTypesLoading } = useQuery({
    queryKey: ['employmentTypes', { size: 100 }],
    queryFn: () => getEmploymentTypes({ size: 100 }),
    enabled: isOpen,
  });

  const employmentTypeOptions = useMemo(() => {
    const allTypes = employmentTypesData?.content ?? [];
    if (!selectedEntity?.employmentTypeIds?.length) return [];
    const entityTypeIds = new Set(selectedEntity.employmentTypeIds);
    return allTypes
      .filter((et) => entityTypeIds.has(et.id))
      .map((et) => ({ label: et.name, value: et.id }));
  }, [employmentTypesData, selectedEntity]);

  const { data: documentTypesData, isLoading: documentTypesLoading } = useQuery({
    queryKey: ['documentTypesAll', { size: 100 }],
    queryFn: () => getDocumentTypes({ size: 100 }),
    enabled: isOpen,
  });

  const documentTypeOptions = useMemo(
    () => (documentTypesData?.content ?? []).map((dt) => ({ label: dt.name, value: dt.id })),
    [documentTypesData],
  );

  // ============================================
  // CREATE MUTATION
  // ============================================

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) throw new Error('No file selected');

      const fileRef = await dsFileService.upload(selectedFile, 'CONTRACT_TEMPLATE');

      const created = await dsTemplateService.create({
        name: watchedName,
        description: watch('description'),
        templateFormat,
        sourceFileId: fileRef.id,
        countryId: watch('countryId'),
        legalEntityId: watch('legalEntityId'),
        employmentTypeId: watch('employmentTypeId'),
        documentTypeId: watch('documentTypeId'),
      });

      if (templateFormat === 'DOCX') {
        await dsTemplateService.scanPlaceholders(created.id).catch(() => {});
      }

      try {
        sessionStorage.setItem(`template-file-${created.id}`, fileRef.id);
      } catch { /* ignore */ }

      return created;
    },
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['dsTemplates'] });
      addNotification({ variant: 'success', title: t('K_DS_NOTIF_TEMPLATE_CREATED_DRAFT', 'Template created as draft') });
      handleClose();
      void navigate({ to: `${routeTo}/${created.id}`, search: { edit: true } });
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  // ============================================
  // HANDLERS
  // ============================================

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isPdf = file.type === 'application/pdf';
    const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      || file.name.endsWith('.docx');

    if (templateFormat === 'PDF' && !isPdf) {
      addNotification({ variant: 'critical', title: t('K_DS_VALIDATION_SELECT_PDF', 'Please select a PDF file') });
      return;
    }
    if (templateFormat === 'DOCX' && !isDocx) {
      addNotification({ variant: 'critical', title: t('K_DS_VALIDATION_SELECT_DOCX', 'Please select a DOCX file') });
      return;
    }

    setSelectedFile(file);
  };

  const handleClose = () => {
    reset(DEFAULT_FORM_VALUES);
    setTemplateFormat('DOCX');
    setSelectedFile(null);
    setSlideDirection('left');
    onClose();
  };

  const handleNext = async () => {
    if (isLastStep) {
      if (!canCreate) {
        addNotification({ variant: 'critical', title: t('K_DS_VALIDATION_FILL_REQUIRED', 'Please fill in template name and upload a file.') });
        return;
      }
      createMutation.mutate();
    } else {
      setSlideDirection('left');
      await goToNext();
    }
  };

  const handlePrev = () => {
    setSlideDirection('right');
    goToPrev();
  };

  // ============================================
  // RENDER
  // ============================================

  const stepClasses = (stepIndex: number) =>
    [
      styles.step,
      currentStep === stepIndex ? styles.stepActive : styles.stepHidden,
      currentStep === stepIndex ? (slideDirection === 'left' ? styles.slideLeft : styles.slideRight) : '',
    ].filter(Boolean).join(' ');

  const footer = (
    <StepperModalFooter
      isFirstStep={isFirstStep}
      isLastStep={isLastStep}
      onNext={handleNext}
      onPrev={handlePrev}
      onCancel={handleClose}
      isSubmitting={createMutation.isPending}
      disableNext={currentStep === 0 && !canProceedScope}
      submitLabel={t('K_DS_BUTTON_CREATE_TEMPLATE', 'Create Template')}
    />
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('K_DS_MODAL_TITLE_NEW_TEMPLATE', 'Create New Template')}
      size="lg"
      footer={footer}
    >
      <Stepper
        steps={stepConfigs}
        activeStep={currentStep}
        onStepClick={goToStep}
      />

      <div className={styles.stepContainer}>
        {/* Step 0: Scope */}
        <div className={stepClasses(0)}>
          <div className={styles.form}>
            <div className={styles.row}>
              <Controller
                name="countryId"
                control={control}
                rules={{ required: t('K_VALIDATION_COUNTRY_REQUIRED', 'Country is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_COUNTRY', 'Country')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_COUNTRY', 'Select country')}
                    options={countryOptions}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      setValue('legalEntityId', '');
                      setValue('employmentTypeId', '');
                    }}
                    error={errors.countryId?.message}
                    loading={countriesLoading}
                    searchable
                  />
                )}
              />
              <Controller
                name="legalEntityId"
                control={control}
                rules={{ required: t('K_VALIDATION_LEGAL_ENTITY_REQUIRED', 'Legal entity is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_LEGAL_ENTITY', 'Legal Entity')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_LEGAL_ENTITY', 'Select legal entity')}
                    options={legalEntityOptions}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      setValue('employmentTypeId', '');
                    }}
                    error={errors.legalEntityId?.message}
                    loading={legalEntitiesLoading}
                    disabled={!watchedCountryId}
                    searchable
                  />
                )}
              />
            </div>

            <div className={styles.row}>
              <Controller
                name="employmentTypeId"
                control={control}
                rules={{ required: t('K_VALIDATION_EMPLOYMENT_TYPE_REQUIRED', 'Employment type is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_EMPLOYMENT_TYPE', 'Employment Type')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_EMPLOYMENT_TYPE', 'Select employment type')}
                    options={employmentTypeOptions}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.employmentTypeId?.message}
                    loading={employmentTypesLoading}
                    disabled={!watchedLegalEntityId}
                  />
                )}
              />
              <Controller
                name="documentTypeId"
                control={control}
                rules={{ required: t('K_VALIDATION_DOCUMENT_TYPE_REQUIRED', 'Document type is required') }}
                render={({ field }) => (
                  <Select
                    label={t('K_LABEL_DOCUMENT_TYPE', 'Document Type')}
                    isRequired
                    placeholder={t('K_PLACEHOLDER_DOCUMENT_TYPE', 'Select document type')}
                    options={documentTypeOptions}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.documentTypeId?.message}
                    loading={documentTypesLoading}
                    searchable
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Step 1: Setup + File Upload */}
        <div className={stepClasses(1)}>
          <div className={styles.form}>
            <TextInput
              label={t('K_DS_LABEL_TEMPLATE_NAME', 'Template Name')}
              isRequired
              placeholder={t('K_DS_PLACEHOLDER_TEMPLATE_NAME', 'e.g., Employment Agreement')}
              error={errors.name?.message}
              {...register('name', {
                required: t('K_DS_VALIDATION_NAME_REQUIRED', 'Template name is required'),
                maxLength: { value: 255, message: t('K_VALIDATION_MAX_255', 'Max 255 characters') },
              })}
            />
            <Textarea
              label={t('K_DS_LABEL_DESCRIPTION', 'Description (optional)')}
              placeholder={t('K_DS_PLACEHOLDER_DESCRIPTION', 'Describe what this template is used for...')}
              {...register('description')}
            />

            <div className={styles.formSection}>
              <label className={styles.label}>{t('K_DS_LABEL_FORMAT', 'Template Format')}</label>
              <div className={styles.formatToggle}>
                <button
                  className={`${styles.formatBtn} ${templateFormat === 'DOCX' ? styles.active : ''}`}
                  onClick={() => { setTemplateFormat('DOCX'); setSelectedFile(null); }}
                  type="button"
                >
                  <div>
                    <div className={styles.formatName}>{t('K_DS_FORMAT_DOCX', 'DOCX Template')}</div>
                    <div className={styles.formatDesc}>
                      {t('K_DS_FORMAT_DOCX_DESC', 'Supports {{placeholder}} text replacement with automatic reflow. Best for contracts, agreements, and letters.')}
                    </div>
                  </div>
                </button>
                <button
                  className={`${styles.formatBtn} ${templateFormat === 'PDF' ? styles.active : ''}`}
                  onClick={() => { setTemplateFormat('PDF'); setSelectedFile(null); }}
                  type="button"
                >
                  <div>
                    <div className={styles.formatName}>{t('K_DS_FORMAT_PDF', 'PDF Template')}</div>
                    <div className={styles.formatDesc}>
                      {t('K_DS_FORMAT_PDF_DESC', 'Upload a pre-formatted PDF. Only drag-and-drop signing fields supported. Best for government forms and pre-designed documents.')}
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className={styles.formSection}>
              <label className={styles.label}>
                {t('K_DS_LABEL_UPLOAD_FILE', 'Upload {format} File').replace('{format}', templateFormat === 'DOCX' ? '.docx' : '.pdf')}
              </label>
              <div
                className={`${styles.uploadArea} ${selectedFile ? styles.hasFile : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click(); } }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={templateFormat === 'DOCX' ? '.docx' : '.pdf'}
                  onChange={handleFileSelect}
                  className={styles.fileInput}
                />
                {selectedFile ? (
                  <div className={styles.selectedFile}>
                    <span className={styles.fileName}>{selectedFile.name}</span>
                    <span className={styles.fileSize}>
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </span>
                    <button
                      className={styles.removeFile}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      type="button"
                    >
                      {t('K_DS_REMOVE', 'Remove')}
                    </button>
                  </div>
                ) : (
                  <div className={styles.uploadPrompt}>
                    <p>{t('K_DS_CLICK_TO_SELECT', 'Click to select a {format} file').replace('{format}', templateFormat === 'DOCX' ? '.docx' : '.pdf')}</p>
                    <p className={styles.uploadHint}>{t('K_DS_OR_DRAG_DROP', 'or drag and drop here')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </Modal>
  );
}
